/**
 * Lift2Fit — the public invitation accept screen (`#/join/:code`).
 *
 * The other end of the admin's "free group" invitation. It is deliberately
 * public and outside the app shell, like the CRM front door: the invitee may
 * have no account yet. Submitting creates the profile through the SAME
 * registration path the normal sign-up uses, then signs the new account in.
 *
 * The invitation itself decides whether the new profile is a lifter or a coach
 * and whether a free/trial plan is attached. It can never grant admin — the API
 * makes that structurally impossible.
 */
import { useState, type FormEvent } from 'react';
import { useSession } from '@/lib/useSession';
import { api } from '@/lib/api';
import { Button } from '@/components/ui';
import { Field, ScreenHeader, StatusChip } from '../components';
import { navigate } from '../router';

export function AdminJoinScreen({ code }: { code: string }) {
  const session = useSession();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    if (displayName.trim() === '' || email.trim() === '' || password.length < 8) {
      setError('A name, an email and a password of at least 8 characters are required.');
      setBusy(false);
      return;
    }
    const result = await api.acceptInvitation(code, {
      email: email.trim(),
      password,
      displayName: displayName.trim(),
    });
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? result.problem?.title ?? 'That invitation could not be accepted.');
      setBusy(false);
      return;
    }
    setDone(
      `Welcome, ${result.data.user.displayName}. Your ${result.data.invitation.profileKind} profile is ready.`,
    );
    // Adopt the session through the normal sign-in path, then land in the app.
    await session.login(email.trim(), password);
    setBusy(false);
    navigate('/dashboard');
  };

  return (
    <div className="l2f-app-root">
      <main className="mx-auto flex min-h-screen w-full max-w-[560px] flex-col justify-center gap-6 px-4 py-10">
        <ScreenHeader
          eyebrow="Lift2Fit invitation"
          title="You are invited"
          badge={<StatusChip tone="accent">Single use</StatusChip>}
          lead="Create your profile to accept. The invitation decides whether you join as a lifter or as a coach, and it cannot grant admin."
        />

        <form
          onSubmit={(event) => void submit(event)}
          data-testid="join-screen"
          className="flex flex-col gap-4 border border-hairline bg-surface p-6"
        >
          <Field label="Name">
            <input
              data-testid="join-name"
              value={displayName}
              autoComplete="name"
              onChange={(event) => setDisplayName(event.target.value)}
              className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="join-email"
              type="email"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="join-password"
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
              className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
            />
          </Field>

          {error ? (
            <p role="alert" data-testid="join-error" className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger">
              {error}
            </p>
          ) : null}
          {done ? (
            <p role="status" data-testid="join-done" className="border-l-2 border-success bg-page-subtle px-3 py-2 text-[13px] text-success">
              {done}
            </p>
          ) : null}

          <Button type="submit" data-testid="join-submit" disabled={busy}>
            {busy ? 'Creating your profile…' : 'Accept invitation'}
          </Button>

          <p className="text-[12px] text-ink-muted">
            Invitation code <span className="font-mono text-ink">{code}</span>. If it has expired or was already
            used, ask the founder for a new one. Already have an account?{' '}
            <a href="#/login" className="font-bold text-accent hover:text-accent-hover">
              Sign in
            </a>
            .
          </p>
        </form>
      </main>
    </div>
  );
}
